// Fabric support matrix.
//
// Every proxied Apect server needs three mods to work: ApectGuard (proxy-IP whitelist),
// Fabric API (ApectGuard depends on it) and FabricProxy-Lite (Velocity player forwarding).
// A Minecraft version is only offered if all three exist for it, which is why the list is
// explicit instead of just mirroring Fabric's loader metadata.
//
// The floor is 1.16.5 because FabricProxy-Lite has no older build - without it a Fabric
// server cannot accept forwarded connections from the proxy at all.
//
// Keep this in sync with apect-guard-fabric/versions.tsv, which builds one ApectGuard jar
// per entry here.
//
// `java` is the runtime the container should pin for this version, stored on the server as
// java_version and passed through as APECT_JAVA_VERSION. It is the Java major from Mojang's
// version manifest, except that 1.17.x is given 17 rather than the 16 Mojang lists: 16 is
// EOL, is not one of the panel's selectable versions, and 1.17 runs fine on 17. Values here
// must stay within SUPPORTED_JAVA_VERSIONS in the startup-flags route.
export const FABRIC_VERSIONS = [
  { id: '26.3', fabricApi: '0.163.0+26.3', proxyLite: 'v2.12.0', java: 25 },
  { id: '26.2', fabricApi: '0.161.0+26.2', proxyLite: 'v2.12.0', java: 25 },
  { id: '26.1.2', fabricApi: '0.155.3+26.1.2', proxyLite: 'v2.12.0', java: 25 },
  { id: '26.1.1', fabricApi: '0.155.3+26.1.2', proxyLite: 'v2.12.0', java: 25 },
  { id: '26.1', fabricApi: '0.155.3+26.1.2', proxyLite: 'v2.12.0', java: 25 },
  { id: '1.21.11', fabricApi: '0.141.6+1.21.11', proxyLite: 'v2.11.0', java: 21 },
  { id: '1.21.10', fabricApi: '0.138.4+1.21.10', proxyLite: 'v2.11.0', java: 21 },
  { id: '1.21.9', fabricApi: '0.134.1+1.21.9', proxyLite: 'v2.11.0', java: 21 },
  { id: '1.21.8', fabricApi: '0.136.1+1.21.8', proxyLite: 'v2.10.1', java: 21 },
  { id: '1.21.7', fabricApi: '0.129.0+1.21.7', proxyLite: 'v2.10.1', java: 21 },
  { id: '1.21.6', fabricApi: '0.128.2+1.21.6', proxyLite: 'v2.10.1', java: 21 },
  { id: '1.21.5', fabricApi: '0.128.2+1.21.5', proxyLite: 'v2.10.1', java: 21 },
  { id: '1.21.4', fabricApi: '0.119.4+1.21.4', proxyLite: 'v2.10.1', java: 21 },
  { id: '1.21.3', fabricApi: '0.114.1+1.21.3', proxyLite: 'v2.10.1', java: 21 },
  { id: '1.21.2', fabricApi: '0.106.1+1.21.2', proxyLite: 'v2.10.1', java: 21 },
  { id: '1.21.1', fabricApi: '0.116.17+1.21.1', proxyLite: 'v2.10.1', java: 21 },
  { id: '1.21', fabricApi: '0.102.0+1.21', proxyLite: 'v2.10.1', java: 21 },
  { id: '1.20.5', fabricApi: '0.97.8+1.20.5', proxyLite: 'v2.8.0', java: 21 },
  { id: '1.20.4', fabricApi: '0.97.3+1.20.4', proxyLite: 'v2.7.0', java: 17 },
  { id: '1.20.3', fabricApi: '0.91.1+1.20.3', proxyLite: 'v2.7.0', java: 17 },
  { id: '1.20.2', fabricApi: '0.91.6+1.20.2', proxyLite: 'v2.7.0', java: 17 },
  { id: '1.20.1', fabricApi: '0.92.12+1.20.1', proxyLite: 'v2.6.0', java: 17 },
  { id: '1.19.4', fabricApi: '0.87.2+1.19.4', proxyLite: 'v2.5.0', java: 17 },
  { id: '1.19.3', fabricApi: '0.76.1+1.19.3', proxyLite: 'v2.4.0', java: 17 },
  { id: '1.19.2', fabricApi: '0.77.0+1.19.2', proxyLite: 'v2.3.0', java: 17 },
  { id: '1.19.1', fabricApi: '0.58.5+1.19.1', proxyLite: 'v2.3.0', java: 17 },
  { id: '1.19', fabricApi: '0.58.0+1.19', proxyLite: 'v2.1.1', java: 17 },
  { id: '1.18', fabricApi: '0.44.0+1.18', proxyLite: 'v1.1.6', java: 17 },
  { id: '1.17.1', fabricApi: '0.46.1+1.17', proxyLite: 'v1.1.5', java: 17 },
  { id: '1.17', fabricApi: '0.36.0+1.17', proxyLite: 'v1.1.4', java: 17 },
  { id: '1.16.5', fabricApi: '0.42.0+1.16', proxyLite: 'v1.1.3', java: 8 },
];

// Newest supported version - what a brand-new Fabric server is provisioned on, and what
// the version installer preselects.
export const FABRIC_DEFAULT_VERSION = FABRIC_VERSIONS[0].id;

const FABRIC_VERSION_MAP = new Map(FABRIC_VERSIONS.map((entry) => [entry.id, entry]));

export function isSupportedFabricVersion(version) {
  return FABRIC_VERSION_MAP.has(String(version || ''));
}

export function fabricVersionInfo(version) {
  return FABRIC_VERSION_MAP.get(String(version || '')) || null;
}

export function supportedFabricVersionIds() {
  return FABRIC_VERSIONS.map((entry) => entry.id);
}

// Modrinth slugs for the two third-party mods we install alongside ApectGuard. Resolved to
// a concrete file at install time so the jar always matches the Minecraft version.
// NeoForge support matrix.
//
// Mirrors the Fabric list above: a version is only offered when we have an ApectNeoProxy
// build for it, since a proxied NeoForge server needs that mod both to accept Velocity's
// forwarded player data and to verify the connection came from an Apect proxy.
//
// Keep in sync with apect-neo-proxy/settings.gradle.kts, which builds one jar per entry.
//
// 1.20.4 is the floor, not 1.20.2: NeoForge only ever shipped betas for the 1.20.2, 1.20.3
// and 1.20.5 lines and none of those publish the moddev artifacts the mod builds against,
// so no jar can be produced for them. 1.21.2 is included because NeoForge has builds for
// it, though only beta ones.
//
// `neoforge` is the NeoForge build each version is pinned to, and `java` is the runtime the
// container pins (must stay within SUPPORTED_JAVA_VERSIONS in the startup-flags route).
export const NEOFORGE_VERSIONS = [
  { id: '26.3', neoforge: '26.3.0.95', java: 25 },
  { id: '26.2', neoforge: '26.2.0.93', java: 25 },
  { id: '26.1', neoforge: '26.1.2.122', java: 25 },
  { id: '1.21.11', neoforge: '21.11.48', java: 21 },
  { id: '1.21.10', neoforge: '21.10.64', java: 21 },
  { id: '1.21.9', neoforge: '21.9.16-beta', java: 21 },
  { id: '1.21.8', neoforge: '21.8.57', java: 21 },
  { id: '1.21.7', neoforge: '21.7.25-beta', java: 21 },
  { id: '1.21.6', neoforge: '21.6.20-beta', java: 21 },
  { id: '1.21.5', neoforge: '21.5.101', java: 21 },
  { id: '1.21.4', neoforge: '21.4.161', java: 21 },
  { id: '1.21.3', neoforge: '21.3.100', java: 21 },
  { id: '1.21.2', neoforge: '21.2.1-beta', java: 21 },
  { id: '1.21.1', neoforge: '21.1.264', java: 21 },
  { id: '1.21', neoforge: '21.0.167', java: 21 },
  { id: '1.20.6', neoforge: '20.6.144', java: 21 },
  { id: '1.20.4', neoforge: '20.4.253', java: 17 },
];

export const NEOFORGE_DEFAULT_VERSION = NEOFORGE_VERSIONS[0].id;

const NEOFORGE_VERSION_MAP = new Map(NEOFORGE_VERSIONS.map((entry) => [entry.id, entry]));

export function isSupportedNeoforgeVersion(version) {
  return NEOFORGE_VERSION_MAP.has(String(version || ''));
}

export function neoforgeVersionInfo(version) {
  return NEOFORGE_VERSION_MAP.get(String(version || '')) || null;
}

export function supportedNeoforgeVersionIds() {
  return NEOFORGE_VERSIONS.map((entry) => entry.id);
}

// Modrinth slugs for the optional extras a NeoForge server can preinstall. ViaForge is the
// NeoForge equivalent of ViaVersion; Simple Voice Chat ships one project covering all loaders.
export const NEOFORGE_VIA_PROJECT = 'viaforge';
export const NEOFORGE_VOICECHAT_PROJECT = 'simple-voice-chat';

export const FABRIC_API_PROJECT = 'fabric-api';
export const FABRIC_PROXY_PROJECT = 'fabricproxy-lite';

// Minecraft version a brand-new server on `template` should be provisioned on, or null
// to let the container pick the latest release itself.
export function initialMinecraftVersionForTemplate(template) {
  return String(template || '').toLowerCase() === 'fabric' ? FABRIC_DEFAULT_VERSION : null;
}
